// Markers stamped on an entering row by the components that render one.
const LABEL_SELECTOR = '[data-entry-label]';
const CARD_SELECTOR = '[data-entry-card]';
const ACCENT_SELECTOR = '[data-entry-accent]';

const SETTLE_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';
const ROW_MS = 300;
const LABEL_MS = 140;
const CARD_MS = 340;
const CARD_DELAY_MS = 100;
const ACCENT_MS = 1400;

export function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function animateEventEntry(row: HTMLElement): () => void {
  const noop = () => {};
  if (typeof row.animate !== 'function' || prefersReducedMotion()) return noop;

  const { height } = row.getBoundingClientRect();
  if (!height) return noop;

  const { paddingTop, paddingBottom } = window.getComputedStyle(row);
  const running: Animation[] = [];
  const play = (
    element: Element,
    keyframes: Keyframe[],
    options: KeyframeAnimationOptions,
  ) => running.push(element.animate(keyframes, options));

  play(
    row,
    [
      { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 },
      { height: `${height}px`, paddingTop, paddingBottom, opacity: 1 },
    ],
    { duration: ROW_MS, easing: SETTLE_EASING },
  );

  const label = row.querySelector(LABEL_SELECTOR);
  if (label) {
    play(label, [{ opacity: 0 }, { opacity: 1 }], {
      duration: LABEL_MS,
      easing: 'ease-out',
    });
  }

  row.querySelectorAll(CARD_SELECTOR).forEach((card) =>
    play(
      card,
      [
        { opacity: 0, transform: 'scale(0.985)' },
        { opacity: 1, transform: 'none' },
      ],
      {
        duration: CARD_MS,
        delay: CARD_DELAY_MS,
        easing: SETTLE_EASING,
        fill: 'backwards',
      },
    ),
  );

  const accent = row.querySelector(ACCENT_SELECTOR);
  if (accent) {
    play(
      accent,
      [
        { opacity: 1, offset: 0 },
        { opacity: 0.85, offset: 0.6 },
        { opacity: 0, offset: 1 },
      ],
      { duration: ACCENT_MS, easing: 'ease-out' },
    );
  }

  return () => running.forEach((animation) => animation.cancel());
}
